import { useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/use-toast'
import { Save, RotateCcw, AlertCircle } from 'lucide-react'
import { WidgetPreview } from './components/WidgetPreview'
import { WidgetSettings } from './components/WidgetSettings'
import { EmbedCode } from './components/EmbedCode'
import { useWidgetConfig } from './hooks/useWidgetConfig'
import type { ChatAssistant } from './types'

interface WidgetTabProps {
  kbId: string
  chatAssistant?: ChatAssistant
  tenantSlug: string
  isLoading?: boolean
  agentMode?: boolean
  agentId?: string
}

export function WidgetTab({ kbId, chatAssistant, tenantSlug, isLoading, agentMode, agentId }: WidgetTabProps) {
  const { toast } = useToast()
  
  // Create a minimal chat assistant object for agent mode if needed
  const effectiveChatAssistant = agentMode && !chatAssistant ? {
    id: kbId,
    name: 'Agent Assistant',
    shared_id: agentId || '',
    authentication_token: '',
    agent: true,
    widget_settings: {},
    chat_title: '',
    header_color: '',
    widget_button_color: '',
    assistant_chat_bubble_color: '',
    assistant_chat_font_color: '',
    profile_chat_bubble_color: '',
    profile_chat_font_color: '',
    show_avatar: true,
    assistant_avatar_url: '',
    user_avatar_url: '',
    profile_icon_url: '',
  } as ChatAssistant : chatAssistant
  
  const {
    widgetConfig,
    hasUnsavedChanges,
    updateConfig,
    resetToDefaults,
    saveConfig,
    isSaving,
    saveError,
  } = useWidgetConfig(kbId, effectiveChatAssistant)

  // Show save success toast
  useEffect(() => {
    if (!isSaving && !saveError && !hasUnsavedChanges && widgetConfig) {
      // Only show if we just saved (config exists but no unsaved changes)
      const timer = setTimeout(() => {
        // This will only trigger after a successful save
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isSaving, saveError, hasUnsavedChanges, widgetConfig])

  // Handle save
  const handleSave = async () => {
    try {
      await saveConfig()
      toast({
        title: 'Settings saved',
        description: 'Widget settings have been saved successfully',
      })
    } catch (error) {
      toast({
        title: 'Failed to save',
        description: 'Could not save widget settings',
        variant: 'destructive',
      })
    }
  }

  // Handle reset
  const handleReset = () => {
    resetToDefaults()
    toast({
      title: 'Settings reset',
      description: 'Widget settings have been reset to defaults',
    })
  }

  // Warn on unsaved changes before navigation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading widget configuration...</div>
      </div>
    )
  }

  // Skip chat_assistant validation if agent mode is enabled
  if (!agentMode && !chatAssistant) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Chat assistant not found. Please ensure your knowledge base has parsed documents.
        </AlertDescription>
      </Alert>
    )
  }
  
  // Validate agent configuration if agent mode is enabled
  if (agentMode && !agentId) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Agent mode is enabled but Agent ID is not configured. Please configure the Agent ID in Settings.
        </AlertDescription>
      </Alert>
    )
  }

  if (!widgetConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Initializing widget configuration...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You have unsaved changes. Click "Save Settings" to persist your changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Main Layout: Preview + Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Widget Preview (66%) */}
        <div className="lg:col-span-2">
          <WidgetPreview config={widgetConfig} tenantSlug={tenantSlug} />
        </div>

        {/* Right: Widget Settings (33%) */}
        <div className="lg:col-span-1">
          <Card className="h-full flex flex-col">
            <CardHeader>
              <CardTitle>Widget Settings</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col space-y-4 overflow-hidden">
              {/* Action Buttons */}
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  disabled={isSaving}
                  className="flex-1"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Reset to Defaults
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}
                  className="flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
              
              {/* Settings Form - Takes remaining height */}
              <div className="flex-1 overflow-hidden">
                <WidgetSettings config={widgetConfig} onUpdate={updateConfig} />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Embed Code Section */}
      <EmbedCode config={widgetConfig} tenantSlug={tenantSlug} kbId={kbId} />
    </div>
  )
}
